// Updated App.tsx with refactored Gate Pass Form integration
import React, { useState, useEffect, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import LoginPage from './LoginPage';
import './LeftSideMenu/AppStyling.css';

// ============================================
// UPDATED IMPORTS - Refactored Gate Pass Form
// ============================================

// Import the refactored Gate Pass Form with error boundary
import { 
  GatePassForm, 
  ErrorBoundary, 
  LoadingComponent 
} from './gatePassRequest'; // Updated path to refactored structure

// Other existing imports remain the same
import { SavedDraftsView } from './workFlow/SavedDraftsView';
import { RequestsView } from './RequestsView';
import CompanyManagement from './companyManagement/';
import { FunctionsManagement } from './functionsManagement/FunctionsManagement';
import { GateManagement } from './gateManagement/';
import { PersonnelManagementView } from './docAI/PersonnelListView';
import { TasksView } from './TasksView/TasksView';
import { ReportsView } from './ReportsView';
import { AnalyticsView } from './AnalyticsView';
import { DashboardView } from './DashboardView';
import {
  DashboardIcon,
  CreateRequestIcon,
  SavedDraftsIcon,
  ViewRequestsIcon,
  MyTasksIcon,
  ReportsIcon,
  CompaniesIcon,
  GateIcon
} from './components/icons/DashboardIcon';
import { Users } from 'lucide-react';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

// ============================================
// INTERFACES (Updated to match refactored types)
// ============================================

interface GatePassRecord {
  id: number;
  name: string;
  email: string;
  companyName: string;
  idDetails: string;
  consignmentDetails: string;
  gateNumber: string;
  detailedDescription?: string;
  approverEmail: string;
  approverUserId?: string;
  status: string;
  submittedAt: string;
  submittedBy: string;
  requesterUserId: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectionReason?: string;
  fileName?: string;
  fileUrl?: string;
  fileSize?: number;
  fileType?: string;
  uploadedAt?: string;
}

type ViewType = 'welcome' | 'dashboard' | 'create' | 'drafts' | 'create-from-draft' | 'view' | 'pending' | 'tasks' | 'reports' | 'analytics' | 'companies' | 'divisions' | 'gates' | 'personnel' | 'debug';

interface StatusMessage {
  type: 'success' | 'error' | null;
  message: string;
}

interface UserInfo {
  name: string;
  email: string;
  companyId?: number;
  companyName?: string;
}

interface DebugInfo {
  filterType?: string;
  currentUserId?: string | null;
  userEmail?: string;
  responseData?: any;
  timestamp?: string;
  databaseTest?: any;
  testTimestamp?: string;
}

interface AuthorizationState {
  isChecking: boolean;
  isAuthorized: boolean | null;
  authMessage: string;
  userInfo?: any;
}

// Updated interface to match the refactored component
interface SubmissionSuccessData {
  requestId: string;
  status: string;
  region: string;
  submissionDate: string;
  gates: string[];
  personnel: string[];
  approvers: string[];
  reviewers: string[];
  estimatedProcessingTime: string;
  emailSent: boolean;
  fileUploaded: boolean;
}

interface SuccessScreenProps {
  submissionData: SubmissionSuccessData;
  onCreateNew: () => void;
  onViewRequests: () => void;
}

function App() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [authStateChecked, setAuthStateChecked] = useState(false);
  const [msalInitialized, setMsalInitialized] = useState(false);
  const [isShowingSubmissionResult, setIsShowingSubmissionResult] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  // Unsaved changes guard for Create Request form
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<{ view: ViewType; draftId?: number } | null>(null);
  const saveDraftTriggerRef = React.useRef<(() => void) | null>(null);

  // Authorization state
  const [authorizationState, setAuthorizationState] = useState<AuthorizationState>({
    isChecking: false,
    isAuthorized: null,
    authMessage: '',
    userInfo: null
  });
  
  // Navigation state
  const [currentView, setCurrentView] = useState<ViewType>('welcome');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Draft-related state
  const [selectedDraftId, setSelectedDraftId] = useState<number | null>(null);
  
  // Application management state
  const [applications, setApplications] = useState<GatePassRecord[]>([]);
  const [pendingApplications, setPendingApplications] = useState<GatePassRecord[]>([]);
  const [isLoadingApplications, setIsLoadingApplications] = useState(false);
  
  // Debug state
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  
  const [submitStatus, setSubmitStatus] = useState<StatusMessage>({
    type: null,
    message: ''
  });

  const [submissionSuccess, setSubmissionSuccess] = useState<SubmissionSuccessData | null>(null);

  // Admin privilege state
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  // Pending task count for sidebar badge
  const [pendingTaskCount, setPendingTaskCount] = useState<number>(0);

  // Enhanced getUserInfo function with company details from authorization state
  const getUserInfo = useCallback((): UserInfo => {
    console.log('🔍 Getting user info with company details...');
    
    if (accounts && accounts.length > 0) {
      const account = accounts[0];
      
      const name = (account.name || 
                   (account.idTokenClaims?.name as string) || 
                   (account.idTokenClaims?.given_name as string) ||
                   '').toString();
      
      const getClaimAsString = (claim: any): string => {
        return claim && typeof claim === 'string' ? claim : '';
      };
      
      const emailClaim = getClaimAsString(account.idTokenClaims?.email);
      const preferredUsernameClaim = getClaimAsString(account.idTokenClaims?.preferred_username);
      const upnClaim = getClaimAsString(account.idTokenClaims?.upn);
      const uniqueNameClaim = getClaimAsString(account.idTokenClaims?.unique_name);
      const usernameClaim = getClaimAsString(account.username);
      
      let email = '';
      if (emailClaim) {
        email = emailClaim;
      } else if (preferredUsernameClaim && preferredUsernameClaim.includes('@')) {
        email = preferredUsernameClaim;
      } else if (upnClaim) {
        email = upnClaim;
      } else if (uniqueNameClaim && uniqueNameClaim.includes('@')) {
        email = uniqueNameClaim;
      } else if (usernameClaim && usernameClaim.includes('@')) {
        email = usernameClaim;
      } else {
        email = usernameClaim || '';
      }
      
      const companyId = authorizationState.userInfo?.companyId;
      const companyName = authorizationState.userInfo?.companyName;
      
      console.log('🔍 User info extracted:', {
        name,
        email,
        companyId,
        companyName,
        authStateLoaded: authorizationState.isAuthorized === true
      });
      
      return { 
        name, 
        email,
        companyId,
        companyName
      };
    }
    
    return { 
      name: '', 
      email: '',
      companyId: undefined,
      companyName: undefined
    };
  }, [accounts, authorizationState.userInfo, authorizationState.isAuthorized]);

  const getCurrentUserId = useCallback((): string | null => {
    const account = accounts[0];
    if (!account) return null;
    
    return account.idTokenClaims?.oid || account.localAccountId || null;
  }, [accounts]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    console.log('🔐 Getting access token...');
    
    if (!msalInitialized) {
      console.error('❌ MSAL not initialized');
      throw new Error('Authentication system not ready');
    }

    const account = accounts[0];
    if (!account) {
      console.error('❌ No authenticated account found');
      throw new Error('No authenticated account found. Please log in again.');
    }

    console.log('✅ Account found:', account.username);

    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['User.Read', 'openid', 'profile'],
        account: account,
      });
      
      console.log('✅ Token acquired silently');
      return response.accessToken;
    } catch (error: unknown) {
      console.log('⚠️ Silent token acquisition failed, trying popup...');
      
      if (error instanceof InteractionRequiredAuthError) {
        try {
          const response = await instance.acquireTokenPopup({
            scopes: ['User.Read', 'openid', 'profile'],
            account: account,
          });
          
          console.log('✅ Token acquired via popup');
          return response.accessToken;
        } catch (popupError) {
          console.error('❌ Popup token acquisition failed:', popupError);
          throw new Error('Authentication failed. Please sign out and sign in again.');
        }
      }
      
      console.error('❌ Token acquisition failed:', error);
      throw new Error('Authentication failed. Please sign out and sign in again.');
    }
  }, [instance, accounts, msalInitialized]);

  // ============================================
  // EXISTING FUNCTIONS (keeping all unchanged)
  // ============================================
  // Check user authorization, loadApplications, etc. - keeping all existing code

  // Check user authorization (existing code...)
  const checkUserAuthorization = useCallback(async () => {
    console.log('🔍 Starting authorization check...');
    
    setAuthorizationState(prev => ({
      ...prev,
      isChecking: true,
      authMessage: 'Checking user authorization...'
    }));

    try {
      const accessToken = await getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/checkUserAuthorization`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Authorization response:', data);
        
        if (data.success && data.authorized) {
          setAuthorizationState({
            isChecking: false,
            isAuthorized: true,
            authMessage: 'Authorization successful',
            userInfo: {
              ...data.user,
              userEmail: data.userEmail
            }
          });
          
          console.log('✅ User authorized with company details:', {
            userId: data.user?.userId,
            userName: data.user?.userName,
            userEmail: data.user?.userEmail,
            companyId: data.user?.companyId,
            companyName: data.user?.companyName,
            userType: data.user?.userType
          });
        } else {
          setAuthorizationState({
            isChecking: false,
            isAuthorized: false,
            authMessage: data.message || 'User not authorized',
            userInfo: {
              userEmail: data.userEmail,
              searchedEmail: data.searchedEmail
            }
          });
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Authorization check failed:', response.status, errorText);
        
        setAuthorizationState({
          isChecking: false,
          isAuthorized: false,
          authMessage: 'Authorization check failed. Please try again or contact your administrator.',
          userInfo: null
        });
      }
    } catch (error) {
      console.error('💥 Authorization check error:', error);
      
      setAuthorizationState({
        isChecking: false,
        isAuthorized: false,
        authMessage: 'Unable to verify authorization. Please try again later.',
        userInfo: null
      });
    }
  }, [getAccessToken]);

  const loadApplications = useCallback(async (filterType: 'requester' | 'approver' | 'all' = 'requester') => {
    // ... keeping existing implementation
    console.log('🔍 Loading applications with filter:', filterType);
    setIsLoadingApplications(true);
    setDebugInfo(null);
    
    try {
      const accessToken = await getAccessToken();
      const currentUserId = getCurrentUserId();
      const userInfo = getUserInfo();
      
      if (!currentUserId) {
        throw new Error('Unable to get current user ID');
      }

      const queryParams = new URLSearchParams();
      queryParams.append('filterType', filterType);
      
      if (filterType === 'requester') {
        queryParams.append('requesterUserId', currentUserId);
      } else if (filterType === 'approver') {
        queryParams.append('approverUserId', currentUserId);
      }
      
      const url = `${API_BASE_URL}/gatepass_list?${queryParams.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        setDebugInfo({
          filterType,
          currentUserId,
          userEmail: userInfo.email,
          responseData: data,
          timestamp: new Date().toISOString()
        });
        
        if (data.success) {
          if (filterType === 'requester') {
            setApplications(data.data);
            setPendingApplications(data.data.filter((app: GatePassRecord) => app.status.toLowerCase() === 'pending'));
          } else if (filterType === 'approver') {
            setPendingApplications(data.data);
          }
        } else {
          setSubmitStatus({
            type: 'error',
            message: data.message || 'Failed to load applications'
          });
          setTimeout(() => {
            setSubmitStatus({ type: null, message: '' });
          }, 5000);
        }
      } else {
        const errorText = await response.text();
        setSubmitStatus({
          type: 'error',
          message: `Failed to load applications (${response.status}): ${errorText}`
        });
        setTimeout(() => {
          setSubmitStatus({ type: null, message: '' });
        }, 5000);
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: `Error loading applications: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
      setTimeout(() => {
        setSubmitStatus({ type: null, message: '' });
      }, 5000);
    } finally {
      setIsLoadingApplications(false);
    }
  }, [getAccessToken, getCurrentUserId, getUserInfo]);

// Fetch pending task count for sidebar badge
const fetchPendingTaskCount = useCallback(async () => {
  try {
    const accessToken = await getAccessToken();
    const params = new URLSearchParams();
    params.append('status', 'PENDING');

    const response = await fetch(`${API_BASE_URL}/my-tasks?${params}`, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    const data = await response.json();
    if (data.success) {
      setPendingTaskCount(data.data?.length || 0);
    }
  } catch (error) {
    console.error('Error fetching task count:', error);
  }
}, [getAccessToken]);

// Load pending task count when authorized, and refresh periodically
useEffect(() => {
  if (authorizationState.isAuthorized) {
    fetchPendingTaskCount();
    const interval = setInterval(fetchPendingTaskCount, 60000); // Refresh every 60s
    return () => clearInterval(interval);
  }
}, [authorizationState.isAuthorized, fetchPendingTaskCount]);


  // ============================================
  // UPDATED HANDLERS FOR REFACTORED COMPONENT
  // ============================================

  const handleSuccessFromSubmission = useCallback((successData: SubmissionSuccessData) => {
    console.log('✅ Gate pass submission successful:', successData);
    setSubmissionSuccess(successData);
    // Clear any existing status messages
    setSubmitStatus({ type: null, message: '' });
    
    // Optionally reload applications to show the new submission
    if (authorizationState.userInfo?.userType === 'company') {
      loadApplications('requester');
    }
  }, [authorizationState.userInfo?.userType, loadApplications]);

 const handleNavigation = useCallback((view: ViewType, draftId?: number) => {
  // Guard: if navigating AWAY from create view with unsaved changes, show confirmation
  if (
    (currentView === 'create' || currentView === 'create-from-draft') &&
    view !== 'create' && view !== 'create-from-draft' &&
    isFormDirty
  ) {
    setPendingNavigation({ view, draftId });
    return; // Don't navigate yet — wait for user confirmation
  }

  // Perform the actual navigation
  performNavigation(view, draftId);
}, [currentView, isFormDirty]); // eslint-disable-line react-hooks/exhaustive-deps

const performNavigation = useCallback((view: ViewType, draftId?: number) => {
  setCurrentView(view);
  setSubmitStatus({ type: null, message: '' });
  setSubmissionSuccess(null);
  setIsShowingSubmissionResult(false);
  setIsFormDirty(false);         // Reset dirty flag on navigation
  setPendingNavigation(null);    // Clear any pending nav
  
  // Handle draft-specific navigation
  if (view === 'create-from-draft' && draftId) {
    setSelectedDraftId(draftId);
  } else if (view === 'create') {
    setSelectedDraftId(null);
  }
  
  // Load data when switching to certain views
  const userType = authorizationState.userInfo?.userType;
  if (view === 'view' || view === 'pending') {
    if (userType === 'company') {
      loadApplications('requester');
    } else if (userType === 'gate' && view === 'pending') {
      loadApplications('approver');
    }
  }

  // Refresh pending task count on any navigation
  fetchPendingTaskCount();
}, [authorizationState.userInfo?.userType, loadApplications, fetchPendingTaskCount]);

// Handlers for the unsaved changes confirmation dialog
const handleDiscardAndNavigate = useCallback(() => {
  if (pendingNavigation) {
    performNavigation(pendingNavigation.view, pendingNavigation.draftId);
  }
}, [pendingNavigation, performNavigation]);

const handleCancelNavigation = useCallback(() => {
  setPendingNavigation(null);
}, []);

const handleSaveDraftAndStay = useCallback(() => {
  setPendingNavigation(null);        // Dismiss the confirmation dialog
  saveDraftTriggerRef.current?.();   // Open the SaveDraftDialog inside GatePassForm
}, []);

  // Draft-related handlers
  const handleEditDraft = (draftId: number) => {
    handleNavigation('create-from-draft', draftId);
  };

  const handleCreateNew = () => {
    handleNavigation('create');
  };

  const handleNavigateToSavedDrafts = () => {
    handleNavigation('drafts');
  };

  const handleDraftSaved = (draftId: number) => {
    console.log('✅ Draft saved with ID:', draftId);
   
  };

  // ============================================
  // OTHER EXISTING HANDLERS (keeping unchanged)
  // ============================================
  // handleLogin, handleLogout, test functions, etc.

  const handleLogin = async () => {
    if (!msalInitialized) return;

    setIsLoginLoading(true);
    setSubmitStatus({ type: null, message: '' });
    
    try {
      const loginRequest = {
        scopes: ['User.Read', 'openid', 'profile', 'email'],
        prompt: 'select_account' as const
      };
      
      await instance.loginPopup(loginRequest);
      
      const newAccounts = instance.getAllAccounts();
      if (newAccounts.length === 0) {
        throw new Error('Login appeared to succeed but no accounts found');
      }
      
      setAuthorizationState({
        isChecking: false,
        isAuthorized: null,
        authMessage: '',
        userInfo: null
      });
      
    } catch (error) {
      console.error('Login failed:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown login error';
      setSubmitStatus({
        type: 'error',
        message: `Login failed: ${errorMessage}. Please try again.`
      });
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    if (!msalInitialized) return;
    
    try {
      const allAccounts = instance.getAllAccounts();
      
      localStorage.clear();
      sessionStorage.clear();
      
      setAuthorizationState({
        isChecking: false,
        isAuthorized: null,
        authMessage: '',
        userInfo: null
      });
      
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
        mainWindowRedirectUri: window.location.origin,
        account: allAccounts[0]
      });
      
    } catch (error) {
      console.error('Logout failed:', error);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
    }
  };

  // ============================================
  // EXISTING useEffect HOOKS (keeping unchanged)
  // ============================================

  // Initialize MSAL properly
  useEffect(() => {
    const initializeMsal = async () => {
      try {
        if (inProgress === 'none') {
          try {
            await instance.handleRedirectPromise();
          } catch (redirectError) {
            console.error('Redirect promise error:', redirectError);
          }
          
          setMsalInitialized(true);
          setAuthStateChecked(true);
        }
      } catch (error) {
        console.error('MSAL initialization error:', error);
        setMsalInitialized(true);
        setAuthStateChecked(true);
      }
    };

    initializeMsal();
  }, [instance, inProgress]);

  // Check authorization when user is authenticated
  useEffect(() => {
    if (isAuthenticated && msalInitialized && authorizationState.isAuthorized === null) {
      checkUserAuthorization();
    }
  }, [isAuthenticated, msalInitialized, authorizationState.isAuthorized, checkUserAuthorization]);

  // Load applications data when component mounts if authenticated and authorized
  useEffect(() => {
    if (isAuthenticated && authorizationState.isAuthorized === true) {
      const userType = authorizationState.userInfo?.userType;
      if (userType === 'company') {
        loadApplications('requester');
      } else if (userType === 'gate') {
        loadApplications('approver');
      }
    }
  }, [isAuthenticated, authorizationState.isAuthorized, authorizationState.userInfo?.userType, loadApplications]);

  // Check admin privileges by calling the dedicated backend endpoint
  useEffect(() => {
    const checkAdmin = async () => {
      if (!isAuthenticated || authorizationState.isAuthorized !== true) {
        setIsAdmin(false);
        return;
      }

      try {
        const accessToken = await getAccessToken();
        const response = await fetch(`${API_BASE_URL}/checkAdmin`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('👑 Admin check response:', data);
          setIsAdmin(data.isAdmin === true);
        } else {
          console.log('❌ Admin check failed with status:', response.status);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('💥 Admin privilege check error:', error);
        setIsAdmin(false);
      }
    };

    checkAdmin();
  }, [isAuthenticated, authorizationState.isAuthorized, getAccessToken]);


  useEffect(() => {
  console.log('🎨 CSS Effect triggered:', isShowingSubmissionResult);
  const appContainer = document.querySelector('.app-container');
  if (appContainer) {
    if (isShowingSubmissionResult) {
      appContainer.classList.add('hide-main-header');
      console.log('🎨 Added hide-main-header class');
    } else {
      appContainer.classList.remove('hide-main-header');
      console.log('🎨 Removed hide-main-header class');
    }
  }
}, [isShowingSubmissionResult]);



  // ============================================
  // UPDATED RENDER MAIN CONTENT WITH ERROR BOUNDARY
  // ============================================

  const renderMainContent = () => {
    if (submissionSuccess) {
      return (
        <ProfessionalSuccessScreen
          submissionData={submissionSuccess}
          onCreateNew={() => {
            setSubmissionSuccess(null);
            handleNavigation('create');
          }}
          onViewRequests={() => {
            setSubmissionSuccess(null);
            handleNavigation('view');
          }}
        />
      );
    }

    if (!authorizationState.isAuthorized) {
      return (
        <LoadingComponent 
          message="Loading user profile..." 
          size="large" 
          fullScreen={false} 
        />
      );
    }

    switch (currentView) {
      case 'welcome':
        return renderWelcome();

      case 'dashboard':
        // ... keeping existing dashboard code unchanged
        return renderDashboard();

      case 'debug':
        // ... keeping existing debug code unchanged
        return renderDebug();

      case 'create':
      case 'create-from-draft':
      return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error('Gate Pass Form Error:', error, errorInfo);
        setSubmitStatus({
          type: 'error',
          message: 'An error occurred in the Gate Pass Form. Please refresh the page.'
        });
      }}
    >
     <GatePassForm
  getUserInfo={getUserInfo}
  getCurrentUserId={getCurrentUserId}
  getAccessToken={getAccessToken}
  onSubmissionSuccess={handleSuccessFromSubmission}
  onStatusUpdate={setSubmitStatus}
  initialDraftId={currentView === 'create-from-draft' ? selectedDraftId : null}
  onDraftSaved={handleDraftSaved}
  onNavigateToSavedDrafts={handleNavigateToSavedDrafts}
  onNavigateToRequests={() => handleNavigation('view')}
  onDirtyStateChange={(dirty: boolean) => setIsFormDirty(dirty)}
  onRegisterSaveDraft={(fn: () => void) => { saveDraftTriggerRef.current = fn; }}
  onSubmissionStateChange={(isSubmitted: boolean, isSuccess?: boolean) => {
    console.log('🔄 App.tsx received submission state change:', { isSubmitted, isSuccess });
    setIsShowingSubmissionResult(isSubmitted);
    if (isSubmitted && isSuccess) setIsFormDirty(false); // Form is clean after successful submit
    console.log('🔄 App.tsx set isShowingSubmissionResult to:', isSubmitted);
  }}
  {...{} as any} // Temporary fix for TypeScript
/>
      
    </ErrorBoundary>
  );

      case 'drafts':
        return (
          <SavedDraftsView
            getAccessToken={getAccessToken}
            onStatusUpdate={setSubmitStatus}
            onEditDraft={handleEditDraft}
            onCreateNew={handleCreateNew}
          />
        );

      case 'view':
        return (
          <RequestsView
            applications={applications}
            isLoadingApplications={isLoadingApplications}
            onNavigation={(view: any) => {
              const validViews: ViewType[] = ['welcome', 'dashboard', 'create', 'drafts', 'create-from-draft', 'view', 'pending', 'tasks', 'reports', 'companies', 'divisions', 'gates', 'personnel', 'debug'];
              if (validViews.includes(view)) {
                handleNavigation(view);
              }
            }}
            getAccessToken={getAccessToken}
            onStatusUpdate={setSubmitStatus}
          />
        );

      case 'pending':
        return (
          <RequestsView
            applications={pendingApplications}
            isLoadingApplications={isLoadingApplications}
            onNavigation={(view: any) => handleNavigation(view)}
            getAccessToken={getAccessToken}
            onStatusUpdate={setSubmitStatus}
            viewType="pending"
          />
        );

      case 'tasks':
        return (
          <TasksView
            getUserInfo={getUserInfo}
            getAccessToken={getAccessToken}
            onStatusUpdate={setSubmitStatus}
          />
        );

      case 'companies':
        return (
          <CompanyManagement
            getAccessToken={getAccessToken}
            onStatusUpdate={setSubmitStatus}
          />
        );

      case 'divisions':
        return (
          <FunctionsManagement
            getAccessToken={getAccessToken}
            onStatusUpdate={setSubmitStatus}
            companyContext={
              authorizationState.userInfo?.companyId
                ? {
                    companyId: authorizationState.userInfo.companyId,
                    companyName: authorizationState.userInfo.companyName || 'My Company'
                  }
                : undefined
            }
            showInternalBanners={true}
          />
        );

      case 'gates':
        return (
          <GateManagement
            getAccessToken={getAccessToken}
            onStatusUpdate={setSubmitStatus}
          />
        );

      case 'personnel':
        return (
          <PersonnelManagementView
            getAccessToken={getAccessToken}
            onStatusUpdate={(status: any) => {
              let mappedType: 'success' | 'error' | null = null;
              
              if (status.type === 'success') {
                mappedType = 'success';
              } else if (status.type === 'error') {
                mappedType = 'error';
              }

              if (mappedType !== null) {
                setSubmitStatus({
                  type: mappedType,
                  message: status.message
                });
              }
            }}
            userInfo={{
              userType: authorizationState.userInfo?.userType || 'company',
              companyId: authorizationState.userInfo?.companyId,
              companyName: authorizationState.userInfo?.companyName,
              userName: authorizationState.userInfo?.userName,
              userEmail: authorizationState.userInfo?.userEmail
            }}
          />
        );

      case 'reports':
  return (
    <ReportsView 
      getAccessToken={getAccessToken}
      onStatusUpdate={setSubmitStatus}
      userInfo={{
        userId: getCurrentUserId() || undefined,
        email: getUserInfo().email,
        companyId: authorizationState.userInfo?.companyId,
        selectedCompanyId: authorizationState.userInfo?.companyId, // Add this for the correct field
        userType: authorizationState.userInfo?.userType || 'company',
        companyName: authorizationState.userInfo?.companyName,
        userName: authorizationState.userInfo?.userName,
        userEmail: authorizationState.userInfo?.userEmail
      }}
    />
  );

      case 'analytics':
        return (
          <AnalyticsView
            getAccessToken={getAccessToken}
            onStatusUpdate={setSubmitStatus}
          />
        );

      default:
        return <div>View not found</div>;
    }
  };

  // ============================================
  // HELPER RENDER FUNCTIONS (keeping existing implementations)
  // ============================================

  const renderWelcome = () => {
    const userType = authorizationState.userInfo?.userType;
    const isCompanyUser = userType === 'company';
    const firstName = (authorizationState.userInfo?.userName || userName || 'User').split(' ')[0];

    return (
      <div style={{
        maxWidth: '640px',
        margin: '0 auto',
        padding: '60px 24px',
      }}>
        {/* Logo + Greeting */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{
            width: '52px',
            height: '52px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #F3694A 0%, #e0553a 100%)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
          }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 600,
            color: '#242424',
            margin: '0 0 10px 0',
            fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
          }}>
            Welcome to GateQx, {firstName}
          </h2>
          <p style={{
            fontSize: '14px',
            color: '#777',
            margin: 0,
            lineHeight: 1.6,
            maxWidth: '480px',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}>
            GateQx manages gate pass requests for secured facilities — from submission through company review, gate evaluation, and final security clearance.
          </p>
        </div>

        {/* Workflow Visual */}
        <div style={{
          background: '#fff',
          border: '1px solid #e8e8e8',
          borderRadius: '10px',
          padding: '24px 20px',
          marginBottom: '28px',
        }}>
          <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
            {[
              { step: '1', title: 'Submit' },
              { step: '2', title: 'Company Review' },
              { step: '3', title: 'Gate Review' },
              { step: '4', title: 'Security Clearance' },
            ].map((item, idx, arr) => (
              <React.Fragment key={idx}>
                <div style={{ flex: 1, textAlign: 'center', maxWidth: '140px' }}>
                  <div style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    background: idx === arr.length - 1 ? '#F3694A' : '#f0f0f0',
                    color: idx === arr.length - 1 ? '#fff' : '#555',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 600,
                    margin: '0 auto 8px',
                  }}>
                    {item.step}
                  </div>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>{item.title}</div>
                </div>
                {idx < arr.length - 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '20px' }}>
                    <svg width="24" height="12" viewBox="0 0 24 12" style={{ flexShrink: 0 }}>
                      <path d="M2 6h18M16 2l4 4-4 4" stroke="#ddd" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Getting Started Instructions */}
        {isCompanyUser && (
          <div style={{
            background: '#fff',
            border: '1px solid #e8e8e8',
            borderRadius: '10px',
            padding: '22px 28px',
            marginBottom: '28px',
          }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#242424',
              margin: '0 0 8px 0',
              fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
            }}>
              Getting Started
            </h3>
            <p style={{
              fontSize: '13px',
              color: '#666',
              margin: 0,
              lineHeight: 1.65,
            }}>
              To create a request, enter your contract details, add visitor/personnel information using AI-assisted data entry, select the required gates and approvers, and submit. Your request will then move through the approval workflow above.
            </p>
          </div>
        )}

        {/* Single CTA */}
        {isCompanyUser && (
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => handleNavigation('create')}
              style={{
                background: '#F3694A',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 32px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.15s ease',
                fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#e0553a'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#F3694A'; }}
            >
              Create a Gate Pass Request
            </button>
            <p style={{ fontSize: '12px', color: '#aaa', marginTop: '12px' }}>
              or use the menu on the left to explore
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderDashboard = () => {
    return (
      <DashboardView
        getAccessToken={getAccessToken}
        onNavigation={handleNavigation}
        userInfo={{
          userId: getCurrentUserId() || undefined,
          email: getUserInfo().email,
          companyId: authorizationState.userInfo?.companyId,
          selectedCompanyId: authorizationState.userInfo?.companyId,
          userType: authorizationState.userInfo?.userType || 'company',
          companyName: authorizationState.userInfo?.companyName,
          userName: authorizationState.userInfo?.userName,
          userEmail: authorizationState.userInfo?.userEmail
        }}
      />
    );
  };

  const renderDebug = () => {
    const userInfoForDebug = getUserInfo();
    
    return (
      <div className="debug-section">
        <h2>Debug Information</h2>
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '20px', 
          borderRadius: '8px', 
          margin: '20px 0',
          fontFamily: 'monospace',
          fontSize: '14px'
        }}>
          <h3>Current User Info (Enhanced):</h3>
          <pre>{JSON.stringify({
            userId: getCurrentUserId(),
            userInfo: userInfoForDebug,
            authorizationState: authorizationState,
            applications: applications.length,
            pendingApplications: pendingApplications.length
          }, null, 2)}</pre>
          
          {debugInfo && (
            <>
              <h3>Latest API Debug Info:</h3>
              <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
            </>
          )}
        </div>
      </div>
    );
  };

  const ProfessionalSuccessScreen: React.FC<SuccessScreenProps> = ({ submissionData, onCreateNew, onViewRequests }) => {
    // ... keeping existing success screen implementation
    return (
      <div style={{
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
        padding: '40px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Success screen content... */}
      </div>
    );
  };

  // ============================================
  // MAIN RENDER (keeping existing structure)
  // ============================================

  // Early returns for loading and authentication states
  if (!authStateChecked || !msalInitialized) {
    return (
      <div className="app-container">
        <div className="form-container">
          <LoadingComponent message="Initializing application..." size="large" fullScreen />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
  return (
    <LoginPage
      onLogin={handleLogin}
      isLoading={isLoginLoading}
      disabled={!msalInitialized}
      errorMessage={submitStatus.type === 'error' ? submitStatus.message : undefined}
    />
  );
}

  if (authorizationState.isChecking) {
    return (
      <div className="app-container">
        <div className="form-container">
          <LoadingComponent message={authorizationState.authMessage} size="large" fullScreen />
        </div>
      </div>
    );
  }

if (authorizationState.isAuthorized === false) {
  // const userInfo = getUserInfo(); // REMOVE THIS LINE
  
  return (
    <div className="app-container">
      <div className="form-container">
        <header className="form-header">
          <h1>Access Denied...</h1>
          {/* Rest of unauthorized content... */}
        </header>
      </div>
    </div>
  );
}

  // If authenticated and authorized, show the main application with sidebar
  const userAccount = accounts[0];
  const userName = userAccount?.name || userAccount?.username || 'User';
  
  const userType = authorizationState.userInfo?.userType;
  const isCompanyUser = userType === 'company';

  const userTypeLoaded = authorizationState.isAuthorized === true && userType;

  // Detect focal_point role from comma-separated role string (handles both legacy "focal point" and new "focal_point")
  const userRoles = (authorizationState.userInfo?.role || '').split(',').map((r: string) => r.trim().toLowerCase());
  const isFocalPoint = isCompanyUser && (userRoles.includes('focal_point') || userRoles.includes('focal point'));

  return (
    <div className="app-container">
      <div className="app-layout">
        {/* Sidebar - keeping existing implementation */}
        <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <h2 className="sidebar-title">GateQx</h2>
            <button 
              className="sidebar-toggle"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              {sidebarCollapsed ? '→' : '←'}
            </button>
          </div>
          
          <nav className="sidebar-nav">
            {isAdmin && (
            <button 
              className={`nav-item ${currentView === 'dashboard' ? 'active' : ''}`}
              onClick={() => handleNavigation('dashboard')}
            >
              <span className="nav-icon">
                <DashboardIcon size={20} />
              </span>
              <span className="nav-text">Dashboard</span>
            </button>
            )}
            
            {/* Company-specific tabs */}
            {userTypeLoaded && isCompanyUser && (
              <>
                <button 
                  className={`nav-item ${currentView === 'create' || currentView === 'create-from-draft' ? 'active' : ''}`}
                  onClick={() => handleNavigation('create')}
                >
                  <span className="nav-icon">
                    <CreateRequestIcon size={20} />
                  </span>
                  <span className="nav-text">Create Request</span>
                </button>
                
                <button 
                  className={`nav-item ${currentView === 'drafts' ? 'active' : ''}`}
                  onClick={() => handleNavigation('drafts')}
                >
                  <span className="nav-icon">
                    <SavedDraftsIcon size={20} />
                  </span>
                  <span className="nav-text">Saved Drafts</span>
                </button>
                
                <button 
                  className={`nav-item ${currentView === 'view' ? 'active' : ''}`}
                  onClick={() => handleNavigation('view')}
                >
                  <span className="nav-icon">
                    <ViewRequestsIcon size={20} />
                  </span>
                  <span className="nav-text">View Requests</span>
                </button>

                <button 
                  className={`nav-item ${currentView === 'personnel' ? 'active' : ''}`}
                  onClick={() => handleNavigation('personnel')}
                >
                  <span className="nav-icon">
                    <Users size={20} />
                  </span>
                  <span className="nav-text">Personnel</span>
                </button>

                {/* Divisions - Focal Point users only (admins manage divisions via Companies) */}
                {isFocalPoint && !isAdmin && (
                <button 
                  className={`nav-item ${currentView === 'divisions' ? 'active' : ''}`}
                  onClick={() => handleNavigation('divisions')}
                >
                  <span className="nav-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                    </svg>
                  </span>
                  <span className="nav-text">Divisions</span>
                </button>
                )}

                {isAdmin && (
                <button 
                  className={`nav-item ${currentView === 'companies' ? 'active' : ''}`}
                  onClick={() => handleNavigation('companies')}
                >
                  <span className="nav-icon">
                    <CompaniesIcon size={20} />
                  </span>
                  <span className="nav-text">Companies</span>
                </button>
                )}

                {isAdmin && (
                <button 
                  className={`nav-item ${currentView === 'gates' ? 'active' : ''}`}
                  onClick={() => handleNavigation('gates')}
                >
                  <span className="nav-icon">
                    <GateIcon size={20} />
                  </span>
                  <span className="nav-text">Gates</span>
                </button>
                )}
              </>
            )}
            
            {/* My Tasks - Available to all users */}

            {userTypeLoaded && (
  <button 
    className={`nav-item ${currentView === 'tasks' ? 'active' : ''}`}
    onClick={() => handleNavigation('tasks')}
  >
    <span className="nav-icon">
      <MyTasksIcon size={20} />
    </span>
    <span className="nav-text">My Tasks</span>
    {pendingTaskCount > 0 && (
      <span className="nav-badge">{pendingTaskCount > 99 ? '99+' : pendingTaskCount}</span>
    )}
  </button>
)}
            
            {/* Reports - Admin only */}
            {userTypeLoaded && isAdmin && (
              <button 
                className={`nav-item ${currentView === 'reports' ? 'active' : ''}`}
                onClick={() => handleNavigation('reports')}
              >
                <span className="nav-icon">
                  <ReportsIcon size={20} />
                </span>
                <span className="nav-text">Reports</span>
              </button>
            )}

            {/* Track Requests - Admin only */}
            {userTypeLoaded && isAdmin && (
              <button 
                className={`nav-item ${currentView === 'analytics' ? 'active' : ''}`}
                onClick={() => handleNavigation('analytics')}
              >
                <span className="nav-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="3" r="2" />
                    <line x1="12" y1="5" x2="12" y2="10" />
                    <circle cx="12" cy="12" r="2" />
                    <line x1="12" y1="14" x2="6" y2="19" />
                    <line x1="12" y1="14" x2="18" y2="19" />
                    <circle cx="6" cy="21" r="2" />
                    <circle cx="18" cy="21" r="2" />
                  </svg>
                </span>
                <span className="nav-text">Track Requests</span>
              </button>
            )}

            {/* Loading state */}
            {!userTypeLoaded && (
              <div style={{ 
                padding: '20px', 
                textAlign: 'center', 
                color: '#6c757d', 
                fontSize: '14px' 
              }}>
                <div className="spinner" style={{ margin: '0 auto 10px', width: '20px', height: '20px' }}></div>
                Loading menu...
              </div>
            )}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="main-content">
          {/* Profile icon - top right */}
          <div className="profile-icon-wrapper">
            <button
              className="profile-icon-btn"
              style={{ backgroundColor: '#9e9e9e' }}
              onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              title={userName}
            >
              {userName.charAt(0).toUpperCase()}
            </button>
            {profileMenuOpen && (
              <>
                <div className="profile-menu-backdrop" onClick={() => setProfileMenuOpen(false)} />
                <div className="profile-menu-popup">
                  <div className="profile-menu-header">
                    <div className="profile-menu-avatar" style={{ backgroundColor: '#9e9e9e' }}>
                      {userName.charAt(0).toUpperCase()}
                    </div>
                    <div className="profile-menu-info">
                      <div className="profile-menu-name">{userName}</div>
                      {authorizationState.userInfo?.companyName && (
                        <div className="profile-menu-company">{authorizationState.userInfo.companyName}</div>
                      )}
                      {getUserInfo().email && (
                        <div className="profile-menu-email">{getUserInfo().email}</div>
                      )}
                    </div>
                  </div>
                  <div className="profile-menu-divider" />
                  <div style={{ textAlign: 'right', padding: '2px 16px 0', fontSize: '10px', color: '#9e9e9e' }}>v 3.3.0</div>
                  <button onClick={() => { setProfileMenuOpen(false); handleLogout(); }} className="profile-menu-logout">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
          
 

          {submitStatus.type && (
            <div className={`status-message ${submitStatus.type}`}>
              {submitStatus.message}
            </div>
          )}

          <div className="content-area">
            {renderMainContent()}
          </div>
        </main>
      </div>

      {/* Unsaved Changes Confirmation Dialog — styled to match SaveDraftDialog */}
      {pendingNavigation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            position: 'relative'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <h2 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '600',
                color: '#1e293b'
              }}>
                Unsaved Changes
              </h2>
              <button
                onClick={handleCancelNavigation}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div style={{ marginBottom: '24px' }}>
              <p style={{
                margin: '0 0 12px 0',
                fontSize: '14px',
                color: '#64748b',
                lineHeight: '1.5'
              }}>
                You have unsaved changes in your gate pass request. If you leave now, your progress will be lost.
              </p>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#64748b',
                lineHeight: '1.5'
              }}>
                Click <strong style={{ color: '#374151' }}>Save Draft</strong> to preserve your work before leaving.
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={handleDiscardAndNavigate}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  color: '#dc2626',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Discard &amp; Leave
              </button>
              <button
                onClick={handleCancelNavigation}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  color: '#64748b',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Stay on Page
              </button>
              <button
                onClick={handleSaveDraftAndStay}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                Save Draft
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;